import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Typography } from '@mui/material';

import { sleep } from '@altirllc/shared/utils/functionalUtils';

import type { TModalsContext } from '@/contexts/ModalsProvider/types';

import { formatPhone, toFullName } from '@/utils/stringUtils';
import { CircularProgress } from '@altirllc/shared/storybook/stories/CircularProgress.stories';

import { GenericModal } from '@/components/modal/GenericModal';
import { GenericModalClose } from '@/components/modal/GenericModalClose';
import { GenericModalFooter } from '@/components/modal/GenericModalFooter';

import type { TMember } from '@/types/member';

import { InfoBox } from '../InfoBox';
import { UserAvatar } from '../UserAvatar';
import { styles } from './MemberModal.styles';

type TTransferOwnershipMemberProps = TModalsContext & {
  member: TMember;
  onSuccess: (member: TMember) => void;
};

export const TransferOwnership = ({ close, member, onSuccess }: TTransferOwnershipMemberProps) => {
  const name = toFullName(member);
  const phone = formatPhone(member.phone);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const closeSelf = () => close('members.transferOwnership');

  const onSubmit = async () => {
    setLoading(true);
    await sleep(500);

    setLoading(false);
    onSuccess(member);
    closeSelf();
  };

  return (
    <GenericModal sx={[styles.modal, styles.body, styles.minHeight]}>
      <GenericModalClose onClick={closeSelf} />

      <Box sx={styles.fields}>
        <Typography variant="h5" sx={styles.marginBottom}>
          {t('members.transferOwnership.title')}
        </Typography>

        <Box sx={styles.boxGrid}>
          <UserAvatar name={name} size={80} />

          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              {name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {phone}
            </Typography>
          </Box>
        </Box>

        <InfoBox color="blue" variant="body2" sx={styles.infoBox}>
          {t('members.transferOwnership.details')}
        </InfoBox>
      </Box>

      <GenericModalFooter placeContent="end">
        <Button variant="outlined" onClick={closeSelf}>
          {t('common.cancel')}
        </Button>

        <Button variant="contained" onClick={onSubmit} disabled={loading}>
          {t('members.transferOwnership.submit')}
          {loading && <CircularProgress />}
        </Button>
      </GenericModalFooter>
    </GenericModal>
  );
};
